import { HTTP } from "@/services/axios";
import store from "@/store"; // Make sure you import your Vuex store

const ANON_KEY = "ontrack_anon_id";
const LAST_UTM_KEY = "ontrack_last_utm_key";

// Generate or get anonId
function generateAnonId() {
  return crypto.randomUUID();
}

export function getAnonId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = generateAnonId();
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

export function setAnonId(id) {
  localStorage.setItem(ANON_KEY, id);
}

// Normalize a raw utm_source (or its absence) into the closed enum the
// backend expects: facebook / google / organic / direct. Anything that
// isn't recognized as facebook/google/organic falls back to click-ID
// presence, then to "direct" — never passed through raw.
function normalizeSource(rawSource, { gclid, fbclid, wbraid }) {
  const s = (rawSource || "").toLowerCase().trim();
  if (["facebook", "fb", "meta", "instagram", "ig"].includes(s)) return "facebook";
  if (["google", "google-ads", "googleads", "adwords"].includes(s)) return "google";
  if (s === "organic") return "organic";
  if (fbclid) return "facebook";
  if (gclid || wbraid) return "google";
  return "direct";
}

// Normalize a raw utm_medium into the closed enum: paid_social / cpc /
// organic / referral / direct. Anything unrecognized (including the
// "website" default App.vue falls back to) is inferred from the
// already-normalized source rather than passed through raw.
function normalizeMedium(rawMedium, source) {
  const m = (rawMedium || "").toLowerCase().trim();
  if (["cpc", "ppc", "paid-search", "paidsearch"].includes(m)) return "cpc";
  if (["paid_social", "paid-social", "paidsocial", "social"].includes(m)) return "paid_social";
  if (["organic", "seo"].includes(m)) return "organic";
  if (["referral", "ref"].includes(m)) return "referral";
  if (m === "direct") return "direct";
  if (source === "facebook") return "paid_social";
  if (source === "google") return "cpc";
  if (source === "organic") return "organic";
  return "direct";
}

// Real campaign name when one was set on the creative; "facebook_ads" as a
// named fallback when a Facebook click is detected (fbclid present) but no
// utm_campaign was configured; otherwise "direct".
function normalizeCampaign(rawCampaign, source, fbclid) {
  const c = (rawCampaign || "").trim();
  if (c && c.toLowerCase() !== "direct") return c;
  if (fbclid && source === "facebook") return "facebook_ads";
  return "direct";
}

// Attribution fields for API calls (e.g. OTP send/verify) that need to tag
// the request with the visitor's traffic source, not just fire an analytics
// event. Reads the same keys App.vue writes on landing (trafficSource /
// trafficSourceMedium / trafficSourceCampaign / click IDs), normalizes them
// to the backend's expected enums, and includes the anonId used by
// trackUTM() so the backend can join this to the richer attribution record
// posted to /attribution/anonymous.
export function getAttributionPayload() {
  const clickIds = {
    gclid: localStorage.getItem("trafficGclid") || "",
    fbclid: localStorage.getItem("trafficFbclid") || "",
    wbraid: localStorage.getItem("trafficWbraid") || "",
  };

  const source = normalizeSource(
    localStorage.getItem("trafficSource"),
    clickIds,
  );
  const medium = normalizeMedium(
    localStorage.getItem("trafficSourceMedium"),
    source,
  );
  const campaign = normalizeCampaign(
    localStorage.getItem("trafficSourceCampaign"),
    source,
    clickIds.fbclid,
  );

  return { source, medium, campaign, anonId: getAnonId() };
}

// GA4 helpers
function getGA4ClientId() {
  const ga = document.cookie.split("; ").find((row) => row.startsWith("_ga="));
  if (!ga) return null;
  const parts = ga.split("=")[1].split(".");
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
}

function getGA4SessionId() {
  const mid = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!mid) return null;
  const cookieName = `_ga_${mid.replace("G-", "")}`;
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(cookieName + "="));
  if (!cookie) return null;
  const parts = cookie.split("=")[1].split(".");
  return parts.length > 2 ? parts[2] : null;
}

// Compose a unique UTM key
function getUTMKey(params) {
  const { source, medium, campaign, term, content, gclid, fbclid } = params;
  // Always create a key, even if source/medium/campaign are empty
  return `${(source || "direct").toLowerCase().trim()}|${(medium || "website")
    .toLowerCase()
    .trim()}|${(campaign || "").toLowerCase().trim()}|${term || ""}|${
    content || ""
  }|${gclid || ""}|${fbclid || ""}`;
}

// Session storage helpers for last UTM
function getLastUTMKey() {
  return sessionStorage.getItem(LAST_UTM_KEY);
}

function setLastUTMKey(key) {
  sessionStorage.setItem(LAST_UTM_KEY, key);
}

// Get logged-in customerId if exists
function getCustomerId() {
  const user = store.getters.getLoggedInUser;
  return user?.id || null;
}

// Track UTM / click only if changed
export async function trackUTM() {
  const anonId = getAnonId();
  const customerId = getCustomerId(); // logged-in user

  console.log("🚀 [UTM] Tracking started");
  console.log("🔹 anonId:", anonId, "customerId:", customerId);

  const params = new URLSearchParams(window.location.search);
  const utmParams = {
    source:
      params.get("utm_source") ||
      params.get("source") ||
      params.get("aff_source") ||
      "direct",

    medium: params.get("utm_medium") || params.get("medium") || "website",

    campaign:
      params.get("utm_campaign") ||
      params.get("campaign") ||
      params.get("campaignid") ||
      params.get("cid") ||
      "",

    utm_term: params.get("utm_term") || params.get("term") || "",
    utm_content: params.get("utm_content"),
    gclid: params.get("gclid"),
    wbraid: params.get("wbraid"),
    fbclid: params.get("fbclid"),
  };

  console.log("🔹 Detected UTM params:", utmParams);

  const utmKey = getUTMKey(utmParams);
  if (!utmKey) {
    console.log("⚪ No UTM / click ID detected, skipping tracking.");
    return;
  }

  const lastUTMKey = getLastUTMKey();
  console.log("🔹 Last UTM key:", lastUTMKey, "Current UTM key:", utmKey);

  if (lastUTMKey === utmKey) {
    console.log("⚪ UTM key unchanged, no API call needed.");
    return; // nothing changed
  }

  const payload = {
    anonId,
    ...utmParams,
    utmKey,
    landing_page: window.location.pathname,
    platform: "web",
    analytics_client_id: getGA4ClientId(),
    analytics_session_id: getGA4SessionId(),
  };

  if (customerId) {
    payload.customerId = customerId; // include if logged in
  }

  console.log("🔹 Sending UTM payload to backend:", payload);

  try {
    await HTTP.post("/attribution/anonymous", payload);
    setLastUTMKey(utmKey); // remember this UTM
    console.log("✅ UTM successfully tracked and stored locally.");
  } catch (err) {
    console.error("❌ UTM tracking failed:", err);
  }
}
